import { status } from "../../services/ntuc/userService/staticData"
import { CompanyBoolean, SignupState } from "./SignupState"

export enum ActionType {
    ADD_PERSONAL_DETAILS = `APP/SIGN_UP/PERSONAL_AND_CONTACT_DETAILS`,
    ADD_ADDRESS_AND_EMPLOYMENT_DETAILS = `APP/SIGN_UP/ADDRESS_AND_EMPLOYMENT_DETAILS`,
    MY_INFO = `APP/MY_INFO`,
    ADD_BANK_DETAILS = `APP/SIGN_UP/BANK_DETAILS`,
    ADD_REFERRAL_DETAILS = `APP/SIGN_UP/REFERRAL_DETAILS`,
    ADD_MEMBERSHIP_ID = `APP/SIGN_UP/MEMBERSHIP_ID`,
    RETRIEVE_DRAFT = `APP/DRAFT/RETRIEVAL`,
    UPDATE_CAMPAIGN = `APP/CAMPAIGN/UPDATE`,
    CLEAR = `APP/SIGNUP/CLEAR`
}

export interface Action {
    type: ActionType,
    value: any
}

export interface disableFields {
    mandatoryFieldsFilled: boolean,
    isSCPR: boolean,
    addressAndEmployment: {
        postalCode: boolean,
        houseNumber: boolean,
        streetName: boolean,
        floorNumber: boolean,
        unitNumber: boolean,
        buildingName: boolean,
        companyName: CompanyBoolean,
        jobTitle: boolean,
    },
    personalDetail: {
        name: boolean,
        cardName: boolean,
        nric: boolean,
        dateOfBirth: boolean,
        gender: boolean,
        mobileNumber: boolean,
        email: boolean,
        race: boolean,
        maritalStatus: boolean,
        educationalStatus: boolean,
    },
}

export const initialState: SignupState = {
    personalDetail: {
        name: ``,
        cardName: ``,
        nric: ``,
        dateOfBirth: null,
        mobileNumber: ``,
        email: ``,
        gender: ``,
        race: {
            value: ``,
            code: ``,
        },
        residentialStatus: {
            value: ``,
            code: ``,
        },
        maritalStatus: {
            value: ``,
            code: ``,
        },
        educationalStatus: {
            value: ``,
            code: ``,
        },
        saved: false,
    },
    addressAndEmploymentDetail: {
        postalCode: ``,
        houseNumber: ``,
        streetName: ``,
        floorNumber: ``,
        unitNumber: ``,
        buildingName: ``,
        jobTitle: ``,
        companyName: {
            name: ``,
            code: ``,
        },
        occupationalGroup: {
            value: ``,
            code: ``,
        },
        monthlySalary: {
            value: ``,
            code: ``,
        },
    },
    bankDetail: {
        accountNumber: ``,
        bankName: {
            value: ``,
            code: ``,
            status: status.ACTIVE,
            noOfDigits: ``,
        },
    },
    referralDetail: {
        referralCode: ``,
        recruiterCode: ``,
    },
    membershipId: undefined,
    disabledFields: {
        myInfo: false,
        addressAndEmploymentDetailMyInfo: {
            postalCode: false,
            houseNumber: false,
            streetName: false,
            floorNumber: false,
            unitNumber: false,
            buildingName: false,
            companyName: {
                code: false,
                name: false,
                businessNature: false,
            },
            jobTitle: false,
        },
        personalDetailMyInfo: {
            name: false,
            cardName: false,
            nric: false,
            dateOfBirth: false,
            gender: false,
            mobileNumber: false,
            email: false,
            race: false,
            maritalStatus: false,
            educationalStatus: false,
        },
    },
    myInfo: {
        isSCPR: false,
        addressAndEmploymentDetail: {
            postalCode: ``,
            houseNumber: ``,
            streetName: ``,
            floorNumber: ``,
            unitNumber: ``,
            buildingName: ``,
            jobTitle: ``,
            companyName: {
                name: ``,
                code: ``,
                businessNature: ``,
            },
            occupationalGroup: {
                value: ``,
                code: ``,
            },
            monthlySalary: {
                value: ``,
                code: ``,
            },
        },
        personalDetail: {
            name: ``,
            cardName: ``,
            nric: ``,
            dateOfBirth: null,
            mobileNumber: ``,
            email: ``,
            gender: ``,
            race: {
                value: ``,
                code: ``,
            },
            residentialStatus: {
                value: ``,
                code: ``,
            },
            maritalStatus: {
                value: ``,
                code: ``,
            },
            educationalStatus: {
                value: ``,
                code: ``,
            },
            saved: false,
        },
    },
    campaign: undefined,
}

export const signupReducer = function (state: SignupState = initialState, action: Action): SignupState {
    switch (action.type) {
        case ActionType.CLEAR:
            return initialState
        case ActionType.ADD_PERSONAL_DETAILS:
            return {
                ...state,
                personalDetail: action.value,
            }
        case ActionType.ADD_ADDRESS_AND_EMPLOYMENT_DETAILS:
            return {
                ...state,
                addressAndEmploymentDetail: action.value,
            }
        case ActionType.MY_INFO: {
            const raceList = [`CN`, `MY`, `IN`, `EU`, `OT`]
            const { user, address } = action.value
            const REGEX = /^S|T/i
            const isSCPR = REGEX.test(user.nricFin ?? ``)
            // mandatory check — drives ONLY the warning banner, never blanks data
            const mandatoryFieldsFilled =
                !!user.name &&
                !!user.nricFin &&
                !!user.dob &&
                !!user.gender &&
                !!user.race &&
                (isSCPR
                    ? !!user.maritalStatus && !!address.postalCode && !!address.street
                    : !!user.occupation && !!user.company?.name)
            const mapRace = (race: string) => (race ? (raceList.includes(race) ? race : `OT`) : ``)
            // lock a field only when MyInfo returned a value AND the field is lockable for this profile
            const addressLocks = {
                postalCode: isSCPR && !!address.postalCode,
                houseNumber: isSCPR && !!address.blockHSENo,
                streetName: isSCPR && !!address.street,
                floorNumber: isSCPR && !!address.floor,
                unitNumber: isSCPR && !!address.unit,
                buildingName: isSCPR && !!address.buildingName,
            }
            const employmentLocks = {
                companyName: {
                    code: false,
                    name: !isSCPR && !!user.company?.name,
                    businessNature: false,
                },
                jobTitle: !isSCPR && !!user.occupation,
            }
            const personalLocks = {
                name: !!user.name,
                cardName: false,
                nric: !!user.nricFin,
                dateOfBirth: !!user.dob,
                gender: !!user.gender,
                mobileNumber: false,
                email: false,
                race: !!user.race,
                maritalStatus: false,
                educationalStatus: false,
            }
            const personalDetail = {
                ...state.personalDetail,
                name: user.name ?? ``,
                cardName: user.name ?? ``,
                nric: user.nricFin ?? ``,
                dateOfBirth: user.dob ?? ``,
                gender: user.gender ?? ``,
                mobileNumber: user.mobileNumber ?? ``,
                email: user.email ?? ``,
                race: {
                    code: mapRace(user.race ?? ``),
                    value: mapRace(user.race ?? ``),
                },
                maritalStatus: {
                    code: user.maritalStatus ?? ``,
                    value: user.maritalStatus ?? ``,
                },
                educationalStatus: {
                    code: user.highestEducationLevel ?? ``,
                    value: user.highestEducationLevel ?? ``,
                },
            }
            const addressAndEmploymentDetail = {
                ...state.addressAndEmploymentDetail,
                postalCode: address.postalCode ?? ``,
                houseNumber: address.blockHSENo ?? ``,
                streetName: address.street ?? ``,
                floorNumber: address.floor ?? ``,
                unitNumber: address.unit ?? ``,
                buildingName: address.buildingName ?? ``,
                companyName: {
                    code: user.company?.code ?? ``,
                    name: user.company?.name ?? ``,
                },
                jobTitle: user.occupation ?? ``,
            }
            return {
                ...state,
                addressAndEmploymentDetail,
                personalDetail,
                disabledFields: {
                    myInfo: true,
                    addressAndEmploymentDetailMyInfo: {
                        ...state.disabledFields.addressAndEmploymentDetailMyInfo,
                        ...addressLocks,
                        companyName: employmentLocks.companyName,
                        jobTitle: employmentLocks.jobTitle,
                    },
                    personalDetailMyInfo: {
                        ...state.disabledFields.personalDetailMyInfo,
                        ...personalLocks,
                    },
                },
                myInfo: {
                    mandatoryFieldsFilled,
                    isSCPR,
                    addressAndEmploymentDetail: { ...addressAndEmploymentDetail },
                    personalDetail: { ...personalDetail },
                },
            }
        }
        case ActionType.ADD_BANK_DETAILS:
            return {
                ...state,
                bankDetail: action.value,
            }
        case ActionType.ADD_REFERRAL_DETAILS:
            return {
                ...state,
                referralDetail: action.value,
            }
        case ActionType.ADD_MEMBERSHIP_ID:
            return {
                ...state,
                membershipId: action.value,
            }
        case ActionType.RETRIEVE_DRAFT:
            return {
                ...state,
                membershipId: action.value.membership[0].id,
                addressAndEmploymentDetail: {
                    ...state.addressAndEmploymentDetail,
                    postalCode: action.value.user.address.postalCode,
                    houseNumber: action.value.user.address.blockHSENo,
                    streetName: action.value.user.address.street,
                    floorNumber: action.value.user.address.floor,
                    unitNumber: action.value.user.address.unit,
                    buildingName: action.value.user.address.buildingName,
                    occupationalGroup: {
                        code: action.value.user.occupationalGroup,
                        value: action.value.user.occupationalGroup,
                    },
                    monthlySalary: {
                        code: action.value.user.monthlyGrossSalary + ``,
                        value: action.value.user.monthlyGrossSalary + ``,
                    },
                    companyName: {
                        code: action.value.user.company?.code,
                        name: action.value.user.company?.name,
                        businessNature: action.value.user.company?.businessNature,
                    },
                    jobTitle: action.value.user.occupation,
                },
                personalDetail: {
                    ...state.personalDetail,
                    name: action.value.user.name,
                    cardName: action.value.membership[0].nameOnCard ?? action.value.user.name,
                    nric: action.value.user.nricFin,
                    dateOfBirth: action.value.user.dob,
                    gender: action.value.user.gender,
                    mobileNumber: action.value.user.mobileNumber,
                    email: action.value.user.email,
                    race: {
                        code: action.value.user.race,
                        value: action.value.user.race,
                    },
                    maritalStatus: {
                        code: action.value.user.maritalStatus,
                        value: action.value.user.maritalStatus,
                    },
                    educationalStatus: {
                        code: action.value.user.highestEducationLevel,
                        value: action.value.user.highestEducationLevel,
                    },
                    residentialStatus: {
                        code: action.value.user.address.residentialStatus,
                        value: action.value.user.address.residentialStatus,
                    },
                },
                bankDetail: {
                    accountNumber: action.value.user.bankAccountNumber,
                    bankName: {
                        value: action.value.user.bankName,
                        code: action.value.user.bankCode,
                        status: action.value.user.bankStatus,
                        noOfDigits: action.value.user.bankNoOfDigits,
                    },
                },
                referralDetail: {
                    referralCode: action.value.membership[0].referralCode,
                    recruiterCode: action.value.membership[0].recruiterCode,
                },
            }
        case ActionType.UPDATE_CAMPAIGN:
            return {
                ...state,
                campaign: {
                    code: action.value.code,
                    app: action.value.app,
                    callback: action.value.callback,
                },
            }
    }
}
